import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { CreditCard, Plus, Trash, PaperPlaneTilt, SealCheck, CloudArrowUp, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const fmtMXN = (n) =>
  `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtFecha = (s) => (s ? String(s).slice(0, 10) : "—");

// La CLABE se guarda y se muestra completa: el cliente la necesita para transferir.
// No es un dato secreto tipo PAN de tarjeta — es la cuenta a la que se deposita.
const soloDigitos = (raw) => (raw || "").replace(/\D/g, "");
const fmtClabe = (raw) => {
  const d = soloDigitos(raw);
  return d.length === 18 ? d.replace(/(.{4})(?=.)/g, "$1 ") : raw || "";
};
// Etiquetas legibles para las llaves conocidas del dict `datos`.
const DATO_LABEL = { banco: "Banco", clabe: "CLABE", clabe_enmascarada: "CLABE", titular: "Beneficiario" };

const ESTADO_BADGE = {
  activa: "bg-emerald-100 text-emerald-800",
  periodo_gracia: "bg-amber-100 text-amber-900",
  suspendida: "bg-red-100 text-red-800",
  cancelada: "bg-slate-200 text-slate-700",
  prueba: "bg-sky-100 text-sky-800",
  sin_suscripcion: "bg-slate-100 text-slate-600",
};
const ESTADO_LABEL = {
  activa: "Activa",
  periodo_gracia: "En periodo de gracia",
  suspendida: "Suspendida (vencida)",
  cancelada: "Cancelada",
  prueba: "Periodo de prueba",
  sin_suscripcion: "Sin suscripción",
};
const MOV_ESTADO_BADGE = {
  pagado: "bg-emerald-100 text-emerald-800",
  conciliado: "bg-emerald-100 text-emerald-800",
  pendiente: "bg-amber-100 text-amber-900",
  parcial: "bg-amber-100 text-amber-900",
  vencido: "bg-red-100 text-red-800",
};
const PERIODICIDAD = ["mensual", "semestral", "anual"];
// 'periodo_gracia' / 'suspendida' se derivan de la vigencia; aquí solo los que se fijan a mano.
const SUSC_ESTADOS = ["activa", "prueba", "cancelada"];
const FORMA_PAGO_TIPOS = ["transferencia", "tarjeta", "domiciliacion", "efectivo", "otro"];
const TITULAR_ROLES = ["rh", "admin_empresa"];

function UsoBar({ label, usados, limite }) {
  const pct = limite ? Math.min(100, Math.round((usados / limite) * 100)) : 0;
  const over = limite != null && usados >= limite;
  return (
    <div>
      <div className="flex justify-between text-xs font-medium text-slate-600 mb-1">
        <span>{label}</span>
        <span className={over ? "text-red-600 font-bold" : ""}>
          {usados} / {limite ?? "∞"}
        </span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${over ? "bg-red-500" : pct > 80 ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: limite ? `${pct}%` : "0%" }}
        />
      </div>
    </div>
  );
}

export default function Suscripcion() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [estado, setEstado] = useState(null);          // GET /suscripciones/mi-estado (rh/admin)
  const [planes, setPlanes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [suscripciones, setSuscripciones] = useState([]);
  const [usuariosRh, setUsuariosRh] = useState([]);
  const [cuenta, setCuenta] = useState({ movimientos: [], saldo: 0, proximo_vencimiento: null });
  const [formasPago, setFormasPago] = useState([]);
  const [busy, setBusy] = useState(false);

  // dialogs
  const [movDlg, setMovDlg] = useState(null);          // {mode: 'cargo'|'abono', form}
  const [fpDlg, setFpDlg] = useState(null);            // {editing, form}
  const [adminForm, setAdminForm] = useState(null);    // form for crear/editar suscripción

  const suscActual = useMemo(() => {
    if (isSuper) return suscripciones.find((s) => s.empresa_id === empresaId) || null;
    return estado?.suscripcion || null;
  }, [isSuper, suscripciones, empresaId, estado]);

  // ---------- loaders ----------
  const loadMiEstado = async () => {
    const r = await api.get("/suscripciones/mi-estado");
    setEstado(r.data);
  };

  const loadSuperBase = async () => {
    const [p, e, s] = await Promise.all([
      api.get("/planes"),
      api.get("/empresas"),
      api.get("/suscripciones"),
    ]);
    setPlanes(p.data);
    setEmpresas(e.data);
    setSuscripciones(s.data);
    if (!empresaId && e.data.length) setEmpresaId(e.data[0].id);
  };

  const loadCuentaYFormas = async (suscId, empId) => {
    if (!suscId) {
      setCuenta({ movimientos: [], saldo: 0, proximo_vencimiento: null });
      setFormasPago([]);
      return;
    }
    const [m, f] = await Promise.all([
      api.get(`/suscripciones/${suscId}/movimientos`),
      api.get(`/suscripciones/${suscId}/formas-pago`),
    ]);
    setCuenta(m.data);
    setFormasPago(f.data);
    if (isSuper && empId) {
      const u = await api.get("/usuarios");
      // Titular de cuenta: RH o Admin Empresa — en empresas pequeñas suele ser la
      // misma persona sin que exista una cuenta de RH separada.
      setUsuariosRh(u.data.filter((x) => TITULAR_ROLES.includes(x.role) && x.empresa_id === empId));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (isSuper) await loadSuperBase();
        else await loadMiEstado();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const suscId = suscActual?.id;
    const empId = isSuper ? empresaId : suscActual?.empresa_id;
    loadCuentaYFormas(suscId, empId).catch((e) =>
      toast.error(formatApiError(e.response?.data?.detail)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suscActual?.id, empresaId]);

  const refreshAll = async () => {
    if (isSuper) {
      // /empresas se recarga también para reflejar cambios de rh_titular_user_id.
      const [s, e] = await Promise.all([api.get("/suscripciones"), api.get("/empresas")]);
      setSuscripciones(s.data);
      setEmpresas(e.data);
    } else {
      await loadMiEstado();
    }
    await loadCuentaYFormas(suscActual?.id, isSuper ? empresaId : suscActual?.empresa_id);
  };

  // ---------- plan info (para la tarjeta) ----------
  const planInfo = useMemo(() => {
    if (!isSuper) return estado?.plan || null;
    return planes.find((p) => p.clave === suscActual?.plan_clave) || null;
  }, [isSuper, estado, planes, suscActual]);

  const uso = isSuper ? suscActual?.uso : estado?.uso;

  const estadoVig = suscActual?.estado_vigencia
    || estado?.estado_vigencia
    || (suscActual ? "activa" : "sin_suscripcion");

  // ---------- acciones ----------
  const openMov = (mode) => {
    const hoy = hoyISO();
    if (mode === "cargo") {
      const mes = new Date().toLocaleDateString("es-MX", { month: "long", year: "numeric" });
      setMovDlg({
        mode,
        form: {
          tipo: "cargo",
          concepto: `Suscripción ${mes}`,
          monto: estado?.cuota_sugerida ?? suscActual?.cuota_base ?? "",
          periodo: hoy.slice(0, 7),
          fecha: hoy,
          fecha_vencimiento: hoy,
        },
      });
    } else {
      setMovDlg({
        mode,
        form: {
          tipo: "abono",
          concepto: "Pago recibido",
          monto: cuenta.saldo > 0 ? cuenta.saldo : "",
          fecha: hoy,
          forma_pago: formasPago.find((f) => f.predeterminada)?.tipo || "transferencia",
          referencia: "",
        },
      });
    }
  };

  const submitMov = async () => {
    setBusy(true);
    try {
      const f = movDlg.form;
      const payload = { ...f, monto: Number(f.monto) };
      if (movDlg.mode === "abono") delete payload.fecha_vencimiento;
      await api.post(`/suscripciones/${suscActual.id}/movimientos`, payload);
      toast.success(movDlg.mode === "cargo" ? "Cargo generado" : "Abono registrado");
      setMovDlg(null);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---------- comprobante de pago (lo sube el cliente, RH/admin_empresa, o super_admin) ----------
  const comprobanteInputRef = useRef(null);
  const [comprobanteTarget, setComprobanteTarget] = useState(null); // id del movimiento destino
  const [subiendoComprobante, setSubiendoComprobante] = useState(null); // id del movimiento en curso

  const abrirSubidaComprobante = (movId) => {
    setComprobanteTarget(movId);
    comprobanteInputRef.current?.click();
  };

  const onComprobanteFileChange = async (e) => {
    const file = e.target.files?.[0];
    const movId = comprobanteTarget;
    e.target.value = ""; // permite volver a elegir el mismo archivo si hace falta
    if (!file || !movId) return;
    setSubiendoComprobante(movId);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      await api.post(`/suscripciones/${suscActual.id}/movimientos/${movId}/comprobante`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Comprobante enviado. Le avisamos al equipo por correo para que lo revise.");
      await refreshAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubiendoComprobante(null);
    }
  };

  const anularMov = async (mid) => {
    if (!window.confirm("¿Anular este movimiento? No se elimina, queda marcado como anulado.")) return;
    try {
      await api.delete(`/suscripciones/${suscActual.id}/movimientos/${mid}`);
      toast.success("Movimiento anulado");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitFp = async () => {
    setBusy(true);
    try {
      const { editing, form } = fpDlg;
      const datos = {};
      if (form.banco) datos.banco = form.banco;
      // La CLABE se guarda completa (18 dígitos). Si dejan el campo vacío al editar se
      // conserva la que ya tenía — salvo que solo existiera la máscara vieja
      // (clabe_enmascarada), que no es un dato usable y se descarta.
      if (form.clabe) {
        const d = soloDigitos(form.clabe);
        if (d.length !== 18) {
          toast.error("La CLABE debe tener 18 dígitos.");
          setBusy(false);
          return;
        }
        datos.clabe = d;
      } else if (editing?.datos?.clabe) {
        datos.clabe = editing.datos.clabe;
      }
      if (form.titular) datos.titular = form.titular;
      const payload = { tipo: form.tipo, alias: form.alias, predeterminada: form.predeterminada, datos };
      if (editing) {
        await api.put(`/suscripciones/${suscActual.id}/formas-pago/${editing.id}`, payload);
        toast.success("Forma de pago actualizada");
      } else {
        await api.post(`/suscripciones/${suscActual.id}/formas-pago`, payload);
        toast.success("Forma de pago agregada");
      }
      setFpDlg(null);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteFp = async (fid) => {
    if (!window.confirm("¿Eliminar esta forma de pago?")) return;
    try {
      await api.delete(`/suscripciones/${suscActual.id}/formas-pago/${fid}`);
      toast.success("Forma de pago eliminada");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const enviarRecordatorio = async () => {
    try {
      const r = await api.post(`/suscripciones/${suscActual.id}/recordatorio`);
      if (r.data.enviado) toast.success(`Recordatorio enviado a: ${r.data.destinatarios.join(", ")}`);
      else toast.message("SMTP no configurado", { description: "No se envió el correo; revisa las variables SMTP_*." });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // ---------- admin suscripción ----------
  const openAdmin = () => {
    if (suscActual) {
      setAdminForm({ ...suscActual, _modo: "editar" });
    } else {
      setAdminForm({
        _modo: "crear",
        empresa_id: empresaId,
        plan_clave: "crecimiento",
        periodicidad: "mensual",
        fecha_inicio: hoyISO(),
        fecha_fin: "",
        dias_gracia: 7,
        estado: "activa",
        notas: "",
      });
    }
  };

  const submitAdmin = async () => {
    setBusy(true);
    try {
      const f = { ...adminForm };
      const modo = f._modo;
      delete f._modo;
      ["dias_gracia", "limite_centros", "limite_empleados", "empleados_incluidos"].forEach((k) => {
        if (f[k] === "" || f[k] == null) delete f[k];
        else f[k] = Number(f[k]);
      });
      ["cuota_base", "cuota_empleado_extra", "minimo_mensual", "alta_unica"].forEach((k) => {
        if (f[k] === "" || f[k] == null) delete f[k];
        else f[k] = Number(f[k]);
      });
      if (!f.fecha_fin) delete f.fecha_fin;
      if (modo === "crear") {
        await api.post("/suscripciones", f);
        toast.success("Suscripción creada");
      } else {
        await api.put(`/suscripciones/${suscActual.id}`, f);
        toast.success("Suscripción actualizada");
      }
      setAdminForm(null);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setRhTitular = async (uid) => {
    try {
      const r = await api.put(`/empresas/${empresaId}/rh-titular`, { user_id: uid || null });
      // Refleja el cambio de inmediato en el estado local (antes de refreshAll).
      setEmpresas((prev) => prev.map((e) => (e.id === empresaId ? r.data : e)));
      toast.success(uid ? "RH titular designado" : "RH titular removido");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const empresaActual = empresas.find((e) => e.id === empresaId);

  // ================= RENDER =================
  return (
    <div className="p-6 md:p-10" data-testid="suscripcion-page">
      <PageHeader
        kicker="Cuenta"
        title="Suscripción"
        subtitle={
          isSuper
            ? "Administra el plan, la vigencia y el estado de cuenta de cada empresa."
            : "Consulta qué incluye tu plan, tu vigencia y tu estado de cuenta."
        }
        actions={
          isSuper && (
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={empresaId} onValueChange={setEmpresaId}>
                <SelectTrigger className="w-56" data-testid="susc-empresa-select"><SelectValue placeholder="Empresa" /></SelectTrigger>
                <SelectContent>
                  {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={openAdmin} className="bg-brand hover:bg-brand-hover" data-testid="susc-admin-btn">
                {suscActual ? "Editar suscripción" : "Crear suscripción"}
              </Button>
            </div>
          )
        }
      />

      {isSuper && !suscActual && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-4 mb-6 text-sm">
          <strong>{empresaActual?.razon_social || "Esta empresa"}</strong> aún no tiene una suscripción configurada.
          Usa “Crear suscripción” para asignarle un plan.
        </div>
      )}

      {/* ---------- Bloque 1: Mi plan ---------- */}
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="susc-plan-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-uppercase mb-1">Plan contratado</div>
            <div className="flex items-center gap-2">
              <CreditCard size={22} weight="duotone" className="text-slate-500" />
              <span className="font-display text-2xl font-black text-ink">
                {planInfo?.nombre || suscActual?.plan_clave || "Sin plan"}
              </span>
              <span className={`text-[11px] uppercase tracking-wider font-bold px-2 py-1 rounded ${ESTADO_BADGE[estadoVig]}`}>
                {ESTADO_LABEL[estadoVig] || estadoVig}
              </span>
            </div>
            {planInfo?.descripcion && <div className="text-sm text-slate-500 mt-1">{planInfo.descripcion}</div>}
          </div>
          <div className="text-sm text-slate-600 space-y-0.5 text-right">
            <div>Periodicidad: <strong className="text-ink capitalize">{suscActual?.periodicidad || "—"}</strong></div>
            <div>Vigente desde: <strong className="text-ink">{fmtFecha(suscActual?.fecha_inicio)}</strong></div>
            <div>Vence: <strong className="text-ink">{fmtFecha(suscActual?.fecha_fin)}</strong></div>
            {suscActual?.dias_gracia != null && <div className="text-xs text-slate-500">{suscActual.dias_gracia} días de gracia</div>}
          </div>
        </div>

        {uso && (
          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <UsoBar label="Empleados activos" usados={uso.empleados_usados} limite={uso.limite_empleados} />
            <UsoBar label="Centros de trabajo" usados={uso.centros_usados} limite={uso.limite_centros} />
          </div>
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm">
          {(planInfo?.features || estado?.features || []).map((f) => (
            <span key={f} className="flex items-center gap-1.5 text-slate-700">
              <SealCheck size={16} weight="fill" className="text-emerald-500" /> {f}
            </span>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-slate-600 flex flex-wrap gap-x-8 gap-y-1">
          <div>Titular de cuenta:{" "}
            <strong className="text-ink">
              {isSuper
                ? (usuariosRh.find((u) => u.id === empresaActual?.rh_titular_user_id)?.name || "sin designar")
                : (estado?.rh_titular?.name || "sin designar")}
            </strong>
          </div>
          <div>Saldo actual:{" "}
            <strong className={cuenta.saldo > 0 ? "text-red-600" : "text-emerald-700"}>{fmtMXN(cuenta.saldo)}</strong>
          </div>
          <div>Próximo vencimiento: <strong className="text-ink">{fmtFecha(cuenta.proximo_vencimiento)}</strong></div>
          {!isSuper && estado?.cuota_sugerida != null && (
            <div>Cuota del periodo: <strong className="text-ink">{fmtMXN(estado.cuota_sugerida)}</strong></div>
          )}
        </div>

        {isSuper && suscActual && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={enviarRecordatorio} data-testid="susc-recordatorio-btn">
              <PaperPlaneTilt size={15} className="mr-1.5" /> Enviar recordatorio de pago
            </Button>
            <div className="flex items-center gap-2">
              <Label className="label-uppercase">Designar titular de cuenta</Label>
              <Select
                value={empresaActual?.rh_titular_user_id || "ninguno"}
                onValueChange={(v) => setRhTitular(v === "ninguno" ? "" : v)}
              >
                <SelectTrigger className="w-52" data-testid="susc-rh-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ninguno">— Sin designar —</SelectItem>
                  {usuariosRh.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {isSuper && usuariosRh.length === 0 && suscActual && (
          <div className="text-xs text-amber-700 mt-2">
            Esta empresa no tiene ningún usuario con rol “Recursos Humanos” o “Admin Empresa”. Créalo en Usuarios y Permisos para poder designarlo.
          </div>
        )}
      </div>

      {/* ---------- Bloque 2: Estado de cuenta ---------- */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-bold text-ink">Estado de cuenta</h2>
          {isSuper && suscActual && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openMov("cargo")} data-testid="susc-cargo-btn">
                <Plus size={15} className="mr-1" /> Generar cargo
              </Button>
              <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => openMov("abono")} data-testid="susc-abono-btn">
                <Plus size={15} className="mr-1" /> Registrar abono
              </Button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fecha</th><th>Concepto</th><th className="text-right">Cargo</th>
                <th className="text-right">Abono</th><th className="text-right">Saldo</th>
                <th>Estado</th><th></th>
              </tr>
            </thead>
            <tbody>
              {cuenta.movimientos.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-500 py-8">Sin movimientos</td></tr>
              )}
              {cuenta.movimientos.map((m) => {
                const esCargo = m.tipo === "cargo" || m.tipo === "ajuste";
                const puedeSubirComprobante = !isSuper && esCargo && !m.anulado && !["pagado", "conciliado"].includes(m.estado);
                return (
                  <tr key={m.id} className={m.anulado ? "opacity-40 line-through" : ""} data-testid={`mov-row-${m.id}`}>
                    <td className="whitespace-nowrap">{fmtFecha(m.fecha)}</td>
                    <td>
                      {m.concepto}
                      {m.fecha_vencimiento && <span className="block text-xs text-slate-400">vence {fmtFecha(m.fecha_vencimiento)}</span>}
                      {m.referencia && <span className="block text-xs text-slate-400">ref: {m.referencia}</span>}
                    </td>
                    <td className="text-right font-mono">{esCargo ? fmtMXN(m.monto) : "—"}</td>
                    <td className="text-right font-mono">{!esCargo ? fmtMXN(m.monto) : "—"}</td>
                    <td className="text-right font-mono">{fmtMXN(m.saldo_corrido)}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded ${MOV_ESTADO_BADGE[m.estado] || "bg-slate-100"}`}>{m.estado}</span>
                      {m.comprobante_pendiente_revision && (
                        <span className="block text-[11px] text-amber-700 mt-0.5">comprobante en revisión</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      {m.comprobante_url && (
                        <a href={m.comprobante_url} target="_blank" rel="noreferrer" title="Ver comprobante" data-testid={`mov-comprobante-${m.id}`}>
                          <Button variant="ghost" size="sm"><LinkSimple size={15} /></Button>
                        </a>
                      )}
                      {puedeSubirComprobante && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={subiendoComprobante === m.id}
                          onClick={() => abrirSubidaComprobante(m.id)}
                          title={m.comprobante_url ? "Reemplazar comprobante" : "Subir comprobante"}
                          data-testid={`mov-subir-comprobante-${m.id}`}
                        >
                          <CloudArrowUp size={15} />
                        </Button>
                      )}
                      {isSuper && !m.anulado && (
                        <Button variant="ghost" size="sm" onClick={() => anularMov(m.id)} data-testid={`mov-anular-${m.id}`}>
                          <Trash size={15} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 text-sm flex justify-end gap-6">
          <span>Saldo actual: <strong className={cuenta.saldo > 0 ? "text-red-600" : "text-emerald-700"}>{fmtMXN(cuenta.saldo)}</strong></span>
        </div>
      </div>
      <input
        ref={comprobanteInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={onComprobanteFileChange}
        data-testid="comprobante-file-input"
      />

      {/* ---------- Bloque 3: Formas de pago ---------- */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-bold text-ink">Formas de pago</h2>
          {isSuper && suscActual && (
            <Button size="sm" variant="outline" onClick={() => setFpDlg({ editing: null, form: { tipo: "transferencia", alias: "", banco: "", clabe: "", titular: "", predeterminada: false } })} data-testid="fp-new-btn">
              <Plus size={15} className="mr-1" /> Agregar
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead><tr><th>Alias</th><th>Tipo</th><th>Datos</th><th>Predeterminada</th>{isSuper && <th></th>}</tr></thead>
            <tbody>
              {formasPago.length === 0 && (
                <tr><td colSpan={isSuper ? 5 : 4} className="text-center text-slate-500 py-8">Sin formas de pago registradas</td></tr>
              )}
              {formasPago.map((f) => (
                <tr key={f.id} data-testid={`fp-row-${f.id}`}>
                  <td className="font-semibold">{f.alias}</td>
                  <td className="capitalize">{f.tipo}</td>
                  <td className="text-xs text-slate-500">
                    {Object.entries(f.datos || {}).map(([k, v]) => {
                      // Registro viejo: solo se guardó la máscara, nunca la CLABE completa.
                      // No la muestres como si fuera un dato válido; deja claro que hay que recapturarla.
                      if (k === "clabe_enmascarada") {
                        if (f.datos?.clabe) return null; // ya hay CLABE completa, ignora la vieja
                        return (
                          <span key={k} className="block text-amber-600">
                            <span className="text-amber-500">CLABE:</span>{" "}
                            {String(v)} · incompleta — edítala para capturarla completa
                          </span>
                        );
                      }
                      return (
                        <span key={k} className="block">
                          <span className="text-slate-400">{DATO_LABEL[k] || k}:</span>{" "}
                          {k === "clabe" ? fmtClabe(v) : String(v)}
                        </span>
                      );
                    })}
                  </td>
                  <td>{f.predeterminada ? <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">Sí</span> : "—"}</td>
                  {isSuper && (
                    <td className="whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setFpDlg({ editing: f, form: { tipo: f.tipo, alias: f.alias, banco: f.datos?.banco || "", clabe: f.datos?.clabe || "", titular: f.datos?.titular || "", predeterminada: f.predeterminada } })}>Editar</Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteFp(f.id)}><Trash size={15} /></Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============ Dialog: movimiento ============ */}
      <Dialog open={!!movDlg} onOpenChange={(o) => !o && setMovDlg(null)}>
        <DialogContent data-testid="mov-dialog">
          <DialogHeader><DialogTitle className="font-display">{movDlg?.mode === "cargo" ? "Generar cargo" : "Registrar abono"}</DialogTitle></DialogHeader>
          {movDlg && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1">
                <Label className="label-uppercase">Concepto</Label>
                <Input value={movDlg.form.concepto} onChange={(e) => setMovDlg({ ...movDlg, form: { ...movDlg.form, concepto: e.target.value } })} />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Monto (MXN)</Label>
                <Input type="number" step="0.01" value={movDlg.form.monto} onChange={(e) => setMovDlg({ ...movDlg, form: { ...movDlg.form, monto: e.target.value } })} data-testid="mov-monto" />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Fecha</Label>
                <Input type="date" value={movDlg.form.fecha} onChange={(e) => setMovDlg({ ...movDlg, form: { ...movDlg.form, fecha: e.target.value } })} />
              </div>
              {movDlg.mode === "cargo" ? (
                <>
                  <div className="space-y-1">
                    <Label className="label-uppercase">Periodo (YYYY-MM)</Label>
                    <Input value={movDlg.form.periodo} onChange={(e) => setMovDlg({ ...movDlg, form: { ...movDlg.form, periodo: e.target.value } })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="label-uppercase">Vence</Label>
                    <Input type="date" value={movDlg.form.fecha_vencimiento} onChange={(e) => setMovDlg({ ...movDlg, form: { ...movDlg.form, fecha_vencimiento: e.target.value } })} />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <Label className="label-uppercase">Forma de pago</Label>
                    <Select value={movDlg.form.forma_pago} onValueChange={(v) => setMovDlg({ ...movDlg, form: { ...movDlg.form, forma_pago: v } })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{FORMA_PAGO_TIPOS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="label-uppercase">Referencia</Label>
                    <Input value={movDlg.form.referencia} onChange={(e) => setMovDlg({ ...movDlg, form: { ...movDlg.form, referencia: e.target.value } })} />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovDlg(null)}>Cancelar</Button>
            <Button onClick={submitMov} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="mov-save">{busy ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Dialog: forma de pago ============ */}
      <Dialog open={!!fpDlg} onOpenChange={(o) => !o && setFpDlg(null)}>
        <DialogContent data-testid="fp-dialog">
          <DialogHeader><DialogTitle className="font-display">{fpDlg?.editing ? "Editar forma de pago" : "Nueva forma de pago"}</DialogTitle></DialogHeader>
          {fpDlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="label-uppercase">Tipo</Label>
                  <Select value={fpDlg.form.tipo} onValueChange={(v) => setFpDlg({ ...fpDlg, form: { ...fpDlg.form, tipo: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMA_PAGO_TIPOS.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="label-uppercase">Alias</Label>
                  <Input value={fpDlg.form.alias} onChange={(e) => setFpDlg({ ...fpDlg, form: { ...fpDlg.form, alias: e.target.value } })} placeholder="BBVA ***1234" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="label-uppercase">Banco</Label>
                  <Input
                    value={fpDlg.form.banco}
                    onChange={(e) => setFpDlg({ ...fpDlg, form: { ...fpDlg.form, banco: e.target.value } })}
                    placeholder="BBVA México"
                    data-testid="fp-banco-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="label-uppercase">CLABE</Label>
                  <Input
                    inputMode="numeric"
                    maxLength={22}
                    value={fpDlg.form.clabe}
                    onChange={(e) => setFpDlg({ ...fpDlg, form: { ...fpDlg.form, clabe: e.target.value } })}
                    placeholder="18 dígitos"
                    data-testid="fp-clabe-input"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="label-uppercase">Nombre del beneficiario</Label>
                  <Input
                    value={fpDlg.form.titular}
                    onChange={(e) => setFpDlg({ ...fpDlg, form: { ...fpDlg.form, titular: e.target.value } })}
                    placeholder="A nombre de quién está la cuenta"
                    data-testid="fp-titular-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400">
                La CLABE se guarda y se muestra completa — el cliente la necesita para transferir.
                {fpDlg.editing?.datos?.clabe && !fpDlg.form.clabe
                  ? " Deja el campo vacío para conservar la actual."
                  : ""}
              </p>
              {fpDlg.editing?.datos?.clabe_enmascarada && !fpDlg.editing?.datos?.clabe && (
                <p className="text-xs text-amber-600">
                  Esta forma de pago se guardó con la CLABE incompleta
                  {" "}(solo terminación {String(fpDlg.editing.datos.clabe_enmascarada).replace(/\D/g, "").slice(-4)}).
                  Captura los 18 dígitos completos para que quede visible.
                </p>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!fpDlg.form.predeterminada} onChange={(e) => setFpDlg({ ...fpDlg, form: { ...fpDlg.form, predeterminada: e.target.checked } })} />
                Marcar como predeterminada
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFpDlg(null)}>Cancelar</Button>
            <Button onClick={submitFp} disabled={busy} className="bg-brand hover:bg-brand-hover">{busy ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Dialog: administrar suscripción ============ */}
      <Dialog open={!!adminForm} onOpenChange={(o) => !o && setAdminForm(null)}>
        <DialogContent className="max-w-2xl" data-testid="susc-admin-dialog">
          <DialogHeader><DialogTitle className="font-display">{adminForm?._modo === "crear" ? "Crear suscripción" : "Editar suscripción"}</DialogTitle></DialogHeader>
          {adminForm && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="label-uppercase">Plan</Label>
                <Select value={adminForm.plan_clave} onValueChange={(v) => setAdminForm({ ...adminForm, plan_clave: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{planes.map((p) => <SelectItem key={p.clave} value={p.clave}>{p.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Periodicidad</Label>
                <Select value={adminForm.periodicidad} onValueChange={(v) => setAdminForm({ ...adminForm, periodicidad: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODICIDAD.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Vigente desde</Label>
                <Input type="date" value={fmtFecha(adminForm.fecha_inicio) === "—" ? "" : fmtFecha(adminForm.fecha_inicio)} onChange={(e) => setAdminForm({ ...adminForm, fecha_inicio: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Vence</Label>
                <Input type="date" value={fmtFecha(adminForm.fecha_fin) === "—" ? "" : fmtFecha(adminForm.fecha_fin)} onChange={(e) => setAdminForm({ ...adminForm, fecha_fin: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Días de gracia</Label>
                <Input type="number" value={adminForm.dias_gracia ?? ""} onChange={(e) => setAdminForm({ ...adminForm, dias_gracia: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Estado</Label>
                <Select value={adminForm.estado} onValueChange={(v) => setAdminForm({ ...adminForm, estado: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SUSC_ESTADOS.map((s) => <SelectItem key={s} value={s}>{ESTADO_LABEL[s] || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Límite centros (vacío = del plan)</Label>
                <Input type="number" value={adminForm.limite_centros ?? ""} onChange={(e) => setAdminForm({ ...adminForm, limite_centros: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Límite empleados (vacío = del plan)</Label>
                <Input type="number" value={adminForm.limite_empleados ?? ""} onChange={(e) => setAdminForm({ ...adminForm, limite_empleados: e.target.value })} />
              </div>
              <div className="col-span-2 space-y-1">
                <Label className="label-uppercase">Notas</Label>
                <Textarea rows={2} value={adminForm.notas || ""} onChange={(e) => setAdminForm({ ...adminForm, notas: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminForm(null)}>Cancelar</Button>
            <Button onClick={submitAdmin} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="susc-admin-save">{busy ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
